import { useEffect, useReducer, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import heroImg from '../assets/images/Samlade_Människor.jpg';

import Hero from '../components/Hero';
import Section from '../components/Section';
import FormGdprCheckbox from '../components/FormGdprCheckbox';

import classes from './GdprConsent.module.scss';
import Button from '../components/Button';
import {
  checkboxInitialState,
  checkboxReducer,
} from '../reducers/checkbox-reducer';

const BASE_API_URL =
  process.env.REACT_APP_BASE_API_URL ||
  'https://europe-west1-admin-publicering.cloudfunctions.net';
const internalRoleApplicantPath = '/updateGdprConsentForInternalRoleApplicant';
const subcontractorPath = '/updateGdprConsentForSubcontractor';

export default function GdprConsent() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccessful, setSubmitSuccessful] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const { entityId } = useParams();
  const [searchParams] = useSearchParams();

  const [endPath, setPath] = useState('');

  const policyRef = useRef<HTMLInputElement>(null);
  const [acceptsPolicyState, dispatchAcceptsPolicy] = useReducer(
    checkboxReducer,
    checkboxInitialState
  );

  useEffect(() => {
    const type = searchParams.get('type');
    if (!type) {
      alert('Missing type');
      return;
    }

    switch (type) {
      case 'internal-role-applicant':
        setPath(internalRoleApplicantPath);
        break;
      case 'subcontractor':
        setPath(subcontractorPath);
        break;
      default:
        alert('Invalid type');
        return;
    }

    if (!entityId || !entityId.trim()) {
      return;
    }
  }, [searchParams, entityId]);

  async function submitHandler(event: any) {
    event.preventDefault();

    if (!acceptsPolicyState.isValid) {
      policyRef.current!.focus();
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError('');
      setSubmitSuccessful(false);
      const response = await fetch(BASE_API_URL + endPath, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: { id: entityId } }),
      });

      if (!response) {
        setSubmitError('Något gick fel, kunde inte uppdatera samtycke.');
        return;
      }

      const responseJson = await response.json();
      const result = responseJson.result;

      if (result.status) {
        setSubmitSuccessful(true);
        dispatchAcceptsPolicy({ type: 'RESET' });
      } else {
        setSubmitError('Något gick fel, kunde inte uppdatera samtycke.');
      }
    } catch (e) {
      setSubmitError('Något gick fel, kunde inte uppdatera samtycke.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <Hero
        image={heroImg}
        imageAlt='A man waving to his shadow'
        headerText={'Förnya GDPR samtycke'}
        text={'Förnya ditt samtycke till att vi sparar dina uppgifter'}
        textPos='left'
        textColor='white'
        imageOverlay='#00000060'
      />
      <Section backgroundColor='black' textColor='white'>
        <div className='row mb-6'>
          <div className='col-12 col-md-10'>
            <p className='text'>
              Förnya ditt samtycke genom att godkänna integritetspolicyn.
            </p>
          </div>
        </div>

        <form onSubmit={submitHandler}>
          <div className={`row mb-6 ${classes['checkbox-row']}`}>
            <div className='col-11 col-sm col-md col-lg'>
              <FormGdprCheckbox
                id='gdpr-checkbox'
                ref={policyRef}
                isValid={acceptsPolicyState.isValid}
                value={acceptsPolicyState.value}
                onBlur={() => dispatchAcceptsPolicy({ type: 'BLUR' })}
                onChange={e =>
                  dispatchAcceptsPolicy({
                    type: 'INPUT',
                    val: e.currentTarget.checked,
                  })
                }
              />
            </div>
          </div>
          <div className={`row ${classes['button-row']}`}>
            <div className='col'>
              <Button color='white' type='submit' disabled={isSubmitting}>
                {isSubmitting ? 'Skickar...' : 'Skicka'}
              </Button>
            </div>
          </div>
          {submitError && (
            <div className={`row ${classes['submit-error-row']}`}>
              <div className='col'>
                <p>{submitError}</p>
              </div>
            </div>
          )}
          {submitSuccessful && (
            <div className={`row ${classes['submit-successful-row']}`}>
              <div className='col'>
                <p>Samtycke uppdaterat!</p>
              </div>
            </div>
          )}
        </form>
      </Section>
    </>
  );
}
